/**
 * DashboardPersonaContext — F2 → Role-Aware Persona Resolution (DB-3)
 *
 * Manages the active persona for the homepage dashboard.
 *
 * Persona is derived from the current user's report permissions fetched
 * by PermissionsContext (shared across the app — no duplicate API call).
 *
 * Resolution priority (highest to lowest):
 *   executiveDashboard  → 'executive'       (Executive Dashboard)
 *   pmDashboard         → 'project_manager' (PM Dashboard)
 *   (fallback)          → 'project_team'    (Project List — not a dashboard shell)
 *
 * The 'project_team' persona is a new value that signals the dashboard
 * page to render the Project Team view (project list, locked filter)
 * rather than any widget shell.
 *
 * Usage:
 *   Wrap the DashboardPage with <DashboardPersonaProvider>.
 *   Components call useDashboardPersona() to read the active persona.
 */

import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { usePermissions } from '@/contexts/PermissionsContext';
import type { ReportPermissions } from '@/types/roles.types';

// ── Persona type ──────────────────────────────────────────────────────────────

/**
 * DashboardPersona covers the three supported dashboard experiences.
 * 'project_team' is not a widget shell — it signals the homepage to render
 * the locked project list view instead.
 */
export type DashboardPersona = 'executive' | 'project_manager' | 'project_team';

export interface PersonaOption {
  value: Exclude<DashboardPersona, 'project_team'>;
  label: string;
  shortLabel: string;
}

/** The two shell personas (excludes project_team which renders a list view). */
export const PERSONA_OPTIONS: PersonaOption[] = [
  { value: 'executive',       label: 'Executive',       shortLabel: 'Exec' },
  { value: 'project_manager', label: 'Project Manager', shortLabel: 'PM'   },
];

// ── Permission-driven persona resolution ─────────────────────────────────────

/**
 * Resolves the primary dashboard persona from the user's role permissions.
 * Priority order: Executive → Project Manager → Project Team (safe fallback).
 */
function resolvePersonaFromPermissions(permissions: ReportPermissions): DashboardPersona {
  if (permissions.executiveDashboard) return 'executive';
  if (permissions.pmDashboard) return 'project_manager';
  return 'project_team'; // safe fallback — everyone can see their projects
}

// ── Context shape ─────────────────────────────────────────────────────────────
export interface DashboardPersonaContextValue {
  /** The currently active persona, derived from the user's role permissions. */
  persona: DashboardPersona;
  /**
   * True when the user is an Executive AND also holds PM dashboard permission.
   * When true, the settings cog shows a secondary "PM Dashboard Widgets" section.
   */
  hasSecondaryPmDashboard: boolean;
  /** True while the permissions are still being fetched from the backend. */
  isLoading: boolean;
  /**
   * Always false — the demo persona toggle is removed from production UI.
   * Kept for backwards compatibility so existing components that check
   * isDemoToggleVisible compile without changes.
   * @deprecated Remove this prop once all references are cleaned up.
   */
  isDemoToggleVisible: false;
}

const DashboardPersonaContext = createContext<DashboardPersonaContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────
export function DashboardPersonaProvider({ children }: { children: ReactNode }) {
  const { reports, isLoading } = usePermissions();

  const { persona, hasSecondaryPmDashboard } = useMemo(() => {
    const resolved = resolvePersonaFromPermissions(reports);
    const secondary =
      resolved === 'executive' && reports.pmDashboard === true;
    return { persona: resolved, hasSecondaryPmDashboard: secondary };
  }, [reports]);

  return (
    <DashboardPersonaContext.Provider
      value={{
        persona,
        hasSecondaryPmDashboard,
        isLoading,
        isDemoToggleVisible: false,
      }}
    >
      {children}
    </DashboardPersonaContext.Provider>
  );
}

// ── Consumer hook ─────────────────────────────────────────────────────────────
export function useDashboardPersona(): DashboardPersonaContextValue {
  const ctx = useContext(DashboardPersonaContext);
  if (!ctx) {
    throw new Error('useDashboardPersona must be used within a DashboardPersonaProvider');
  }
  return ctx;
}
